import { createContext, useContext, useReducer, useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

const ModalContext = createContext(null)

// Instance counter for unique IDs (supports multiple instances of same modal type)
let instanceCounter = 0

function generateInstanceId(modalType) {
    return `${modalType}_${++instanceCounter}`
}

const initialState = {
    modals: {}, // { [instanceId]: { modalType, isOpen, props, resolve } }
}

function modalReducer(state, action) {
    switch (action.type) {
        case 'OPEN_MODAL':
            return {
                ...state,
                modals: {
                    ...state.modals,
                    [action.instanceId]: {
                        modalType: action.modalType,
                        isOpen: true,
                        props: action.props,
                        resolve: action.resolve,
                    },
                },
            }

        case 'CLOSE_MODAL': {
            const { [action.instanceId]: removed, ...rest } = state.modals
            return { ...state, modals: rest }
        }

        case 'CLOSE_ALL_MODALS':
            // Resolve all pending promises with undefined before clearing
            Object.values(state.modals).forEach((modal) => {
                if (modal.resolve) {
                    modal.resolve(undefined)
                }
            })
            return { ...state, modals: {} }

        case 'UPDATE_MODAL':
            if (!state.modals[action.instanceId]) return state
            return {
                ...state,
                modals: {
                    ...state.modals,
                    [action.instanceId]: {
                        ...state.modals[action.instanceId],
                        props: { ...state.modals[action.instanceId].props, ...action.props },
                    },
                },
            }

        default:
            return state
    }
}

export function ModalProvider({ children }) {
    const [state, dispatch] = useReducer(modalReducer, initialState)
    const location = useLocation()

    // Ref to avoid stale state in callbacks
    const modalsRef = useRef(state.modals)
    useEffect(() => {
        modalsRef.current = state.modals
    }, [state.modals])

    // Store previous pathname to detect route changes
    const prevPathRef = useRef(location.pathname)

    // Close all modals on route change
    useEffect(() => {
        if (prevPathRef.current !== location.pathname) {
            dispatch({ type: 'CLOSE_ALL_MODALS' })
            prevPathRef.current = location.pathname
        }
    }, [location.pathname])

    const show = useCallback((modalType, props = {}) => {
        const instanceId = generateInstanceId(modalType)

        return new Promise((resolve) => {
            dispatch({
                type: 'OPEN_MODAL',
                instanceId,
                modalType,
                props: { ...props, _instanceId: instanceId },
                resolve,
            })
        })
    }, [])

    const hide = useCallback((instanceId, result) => {
        // Use ref to avoid stale state
        const modal = modalsRef.current[instanceId]
        if (modal?.resolve) {
            modal.resolve(result)
        }
        dispatch({ type: 'CLOSE_MODAL', instanceId })
    }, [])

    const update = useCallback((instanceId, props) => {
        dispatch({ type: 'UPDATE_MODAL', instanceId, props })
    }, [])

    const value = {
        modals: state.modals,
        show,
        hide,
        update,
    }

    return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
}

export function useModalContext() {
    const context = useContext(ModalContext)
    if (!context) {
        throw new Error('useModalContext must be used within ModalProvider')
    }
    return context
}
